import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError, API_BASE } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Users, CheckCircle2, Clock, FileWarning, CalendarClock, Ban, Download, ArrowRight } from "lucide-react";
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";

function StatCard({ icon: Icon, label, value, tone, testid }) {
  return (
    <Card className="animate-fade-up">
      <CardContent className="flex items-center gap-4 p-5">
        <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-md ${tone}`}>
          <Icon className="h-5 w-5" />
        </div>
        <div>
          <div className="font-heading text-2xl font-extrabold text-navy" data-testid={testid}>{value}</div>
          <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, []);

  if (!stats) return <div data-testid="admin-dashboard-loading" className="text-sm text-muted-foreground">Loading dashboard…</div>;

  const ragData = [
    { name: "Green", value: stats.rag.green, color: "#10B981" },
    { name: "Amber", value: stats.rag.amber, color: "#F59E0B" },
    { name: "Red", value: stats.rag.red, color: "#EF4444" },
  ];

  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="font-heading text-2xl font-extrabold text-navy sm:text-3xl">Compliance Dashboard</h1>
          <p className="mt-1 text-sm text-muted-foreground">Network-wide subcontractor compliance at a glance.</p>
        </div>
        <Button asChild data-testid="export-report-button" className="bg-brand font-semibold text-white transition-transform duration-150 hover:-translate-y-px hover:bg-brand-dark">
          <a href={`${API_BASE}/admin/export`} download>
            <Download className="mr-2 h-4 w-4" /> Export compliance report
          </a>
        </Button>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
        <StatCard icon={Users} label="Subcontractors" value={stats.total_subcontractors} tone="bg-navy/5 text-navy" testid="stat-total-subs" />
        <StatCard icon={CheckCircle2} label="Approved" value={stats.approved_subcontractors} tone="bg-emerald-50 text-emerald-600" testid="stat-approved-subs" />
        <StatCard icon={Clock} label="Pending Reviews" value={stats.pending_reviews} tone="bg-amber-50 text-amber-600" testid="stat-pending-reviews" />
        <StatCard icon={FileWarning} label="Expired Docs" value={stats.expired_documents} tone="bg-red-50 text-red-600" testid="stat-expired-docs" />
        <StatCard icon={CalendarClock} label="Expiring ≤ 30d" value={stats.expiring_within_30} tone="bg-orange-50 text-orange-600" testid="stat-expiring-30" />
        <StatCard icon={Ban} label="Suspended" value={stats.suspended} tone="bg-slate-100 text-slate-600" testid="stat-suspended" />
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Card className="animate-fade-up lg:col-span-1" data-testid="rag-chart-card">
          <CardHeader><CardTitle className="text-base">RAG compliance split</CardTitle></CardHeader>
          <CardContent>
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={ragData} dataKey="value" innerRadius={48} outerRadius={72} paddingAngle={3} strokeWidth={0}>
                    {ragData.map((d) => <Cell key={d.name} fill={d.color} />)}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-2 grid grid-cols-3 gap-2 text-center">
              {ragData.map((d) => (
                <div key={d.name} data-testid={`rag-count-${d.name.toLowerCase()}`} className="rounded-md border border-border py-2">
                  <div className="font-heading text-xl font-extrabold" style={{ color: d.color }}>{d.value}</div>
                  <div className="text-xs font-medium text-muted-foreground">{d.name}</div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card className="animate-fade-up lg:col-span-2" data-testid="quick-actions-card">
          <CardHeader><CardTitle className="text-base">Review queue</CardTitle></CardHeader>
          <CardContent className="flex h-full flex-col justify-between gap-6">
            <p className="text-sm text-muted-foreground">
              {stats.pending_reviews > 0
                ? `${stats.pending_reviews} document(s) are waiting for review. Approving or rejecting them updates carrier compliance automatically.`
                : "No documents waiting for review. All caught up."}
            </p>
            <div className="flex flex-wrap gap-3">
              <Button asChild data-testid="review-carriers-button" className="bg-navy text-white hover:bg-navy-light">
                <Link to="/admin/carriers">Review carriers <ArrowRight className="ml-1 h-4 w-4" /></Link>
              </Button>
              <Button asChild variant="outline" data-testid="view-email-log-button">
                <Link to="/admin/emails">View notification log</Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
